import AdUnitsOutlinedIcon from '@mui/icons-material/AdUnitsOutlined';
import BrowserUpdatedOutlinedIcon from '@mui/icons-material/BrowserUpdatedOutlined';
import CookieOutlinedIcon from '@mui/icons-material/CookieOutlined';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { bigNumberWithCommas } from '@openx/utils/lib/bigNumberWithCommas.ts';
import { InfoIcon } from '@phosphor-icons/react/Info';
import { useTranslation } from 'react-i18next';

export type SourceIdReachProps = {
  isCustomExportSelected: boolean;
  loading: boolean;
  cookie: number;
  cookieError: boolean;
  device: number;
  deviceError: boolean;
  IDL: number;
  IDLError: boolean;
  liveintent: number;
  liveintentError: boolean;
  ID5: number;
  ID5Error: boolean;
  IP: number;
  IPError: boolean;
};

export const SourceIdReach = ({
  isCustomExportSelected,
  loading,
  cookie,
  cookieError,
  device,
  deviceError,
  IDL,
  IDLError,
  liveintent,
  liveintentError,
  ID5,
  ID5Error,
  IP,
  IPError,
}: SourceIdReachProps) => {
  const { t } = useTranslation();

  const sourceIDConfig = [
    { name: t('COOKIES'), icon: CookieOutlinedIcon, value: cookie, error: cookieError },
    { name: t('DEVICES'), icon: AdUnitsOutlinedIcon, value: device, error: deviceError },
    { name: t('RAMPIDS'), icon: BrowserUpdatedOutlinedIcon, value: IDL, error: IDLError },
    { name: t('NONIDS'), icon: BrowserUpdatedOutlinedIcon, value: liveintent, error: liveintentError },
    { name: t('ID5'), icon: BrowserUpdatedOutlinedIcon, value: ID5, error: ID5Error },
    { name: t('IP'), icon: BrowserUpdatedOutlinedIcon, value: IP, error: IPError },
  ] as const;

  const showSourceIDPanel = !!(
    isCustomExportSelected &&
    ((!loading && (cookie || device || IDL || liveintent || ID5 || IP)) || loading)
  );

  if (!showSourceIDPanel) {
    return null;
  }

  return (
    <Box
      sx={{
        borderTop: theme => `1px solid ${theme.palette.divider}`,
        pt: 3,
        mt: 3,
      }}
    >
      <Typography variant="body2Bold" sx={{ color: 'text.secondary' }}>
        {t('Estimated Source ID Reach')}
      </Typography>

      {loading ? (
        <CircularProgress size={'16px'} />
      ) : (
        <Grid
          container
          spacing={3}
          sx={{
            pt: 2,
          }}
        >
          {sourceIDConfig.map(
            ({ name, icon: Icon, value, error }) =>
              ((value > 0 && !error) || error) && (
                <Grid size={{ xs: 12, sm: 4 }} key={name}>
                  <Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        pb: 1,
                      }}
                    >
                      <Icon sx={{ color: 'text.secondary', fontSize: 18 }} />
                      <Typography variant="caption">{name}</Typography>
                    </Box>

                    {error ? (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <Typography variant="h3">{t('N / A')}</Typography>

                        <Tooltip title={t('Unable to generate reach estimates')}>
                          <IconButton size="large">
                            <InfoIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : (
                      <Typography variant="h2">{bigNumberWithCommas(value)}</Typography>
                    )}
                  </Box>
                </Grid>
              )
          )}
        </Grid>
      )}
    </Box>
  );
};
